import { useState, useEffect, useRef, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStrings } from '../locales';
import { scoreMatch } from '../search/fuzzySearch';
import { buildIndex, SearchEntry, stripMarkdown } from '../search/searchIndex';
import { useContentVersion } from '../hooks/ContentVersionContext';

export { buildIndex };

// One index per content version; the active one follows the HF / Void toggle.
const searchIndexes = { hf: buildIndex('hf'), void: buildIndex('void') };

export default function SearchBar() {
  const [version] = useContentVersion();
  const searchIndex = searchIndexes[version];
  const s = useStrings();
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<SearchEntry[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const [focused, setFocused] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const search = useCallback((q: string) => {
    if (!q.trim()) { setResults([]); return; }

    const found = searchIndex
      .map((item) => {
        const titleScore = scoreMatch(item.title, q) * 2;
        const textScore = scoreMatch(item.text, q);
        return { ...item, _score: Math.max(titleScore, textScore) };
      })
      .filter((item) => item._score! > 0)
      .sort((a, b) => b._score! - a._score!)
      .slice(0, 12);

    setResults(found);
  }, [searchIndex]);

  useEffect(() => {
    search(query);
    setActiveIdx(-1);
  }, [query, search]);

  useEffect(() => {
    const collapse = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setFocused(false);
      }
    };
    // mousedown covers desktop; touchstart covers mobile
    document.addEventListener('mousedown', collapse);
    document.addEventListener('touchstart', collapse);
    return () => {
      document.removeEventListener('mousedown', collapse);
      document.removeEventListener('touchstart', collapse);
    };
  }, []);

  const handleSelect = (path: string) => {
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
    navigate(path);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && results[activeIdx]) {
        handleSelect(results[activeIdx].path);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
      setFocused(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div
      className={`header-search${focused ? ' header-search--focused' : ''}`}
      ref={ref}
      onClick={() => { if (!focused) { setFocused(true); inputRef.current?.focus(); } }}
    >
      <span className="search-icon"><Search size={15} /></span>
      <label htmlFor="global-search" className="sr-only">{s.search.label}</label>
      <input
        ref={inputRef}
        type="text"
        id="global-search"
        name="global-search"
        placeholder={s.search.placeholder}
        value={query}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setFocused(true); }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls="global-search-listbox"
        aria-autocomplete="list"
        aria-activedescendant={activeIdx >= 0 ? `search-result-${activeIdx}` : undefined}
      />
      {open && results.length > 0 && (
        <div
          className="search-dropdown"
          id="global-search-listbox"
          role="listbox"
          aria-label={s.search.resultsAria}
        >
          {results.map((r, i) => (
            <div
              key={i}
              className={`search-result-item${i === 0 ? ' search-result-item--top' : ''}`}
              onClick={() => handleSelect(r.path)}
              role="option"
              id={`search-result-${i}`}
              aria-selected={i === activeIdx}
            >
              <div className="result-name">{r.title}</div>
              <div className="result-category">{r.category}</div>
              {(r.excerpt || r.text) && (
                <div className="result-excerpt">
                  {(r.excerpt || stripMarkdown(r.text)).substring(0, 90)}…
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {open && results.length > 0 ? `${results.length} results found` : ''}
      </div>
    </div>
  );
}
